import { t } from '@/features/i18n/utils';
import { SupplementBreadcrumbs } from '@/features/supplements/components/supplement-breadcrumbs';
import { createFileRoute } from '@tanstack/react-router';
import { Icon } from '@/ui/components/icon';
import { FolderIcon } from '@hugeicons-pro/core-stroke-rounded';
import { BreadcrumbPage } from '@/ui/components/breadcrumb';
import { HeaderBackButton, HeaderChatButton, Header } from '@/ui/components/header';
import { MainHeader, MainHeaderBackButton, MainScrollArea } from '@/ui/components/main';
import { H1 } from '@/ui/components/heading';
import { FilesPageList } from '@/features/documents/components/files-page/files-page-list';
import {
  FilesPageAddFilesButton,
  FilesPageCount,
} from '@/features/documents/components/files-page/files-page-header';
import { FileUploadDialog } from '@/features/documents/components/file-upload';
import { IndexingStatusAlerts } from '@/features/documents/components/indexing-status-alerts';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/contract-management/$supplementId/files',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { projectId, workspaceId, supplementId } = Route.useParams();
  const filterOptions = [
    {
      value: 'MINUTES_OF_MEETING',
      label: t({ en: 'Meeting notes', nb: 'Møtereferater' }),
      description: t({
        en: 'Minutes, agendas, decisions and action items',
        nb: 'Referater, agendaer, beslutninger og aksjonspunkter',
      }),
    },
    {
      value: 'CHANGE_ORDERS',
      label: t({ en: 'Change records', nb: 'Endringsposter' }),
      description: t({
        en: 'Formal change requests, approvals, variations and amendments',
        nb: 'Formelle endringsanmodninger, godkjenninger, endringer og tilleggsavtaler',
      }),
    },
    {
      value: 'EMAIL',
      label: t({ en: 'Correspondence', nb: 'Korrespondanse' }),
      description: t({
        en: 'Letters and emails: questions, notices and replies',
        nb: 'Brev og e-poster: spørsmål, varsler og svar',
      }),
    },
    {
      value: 'INVOICE',
      label: t({ en: 'Invoices', nb: 'Fakturaer' }),
      description: t({
        en: 'Bills and payment requests',
        nb: 'Regninger og betalingsanmodninger',
      }),
    },
    {
      value: 'EXPENSE_CLAIM',
      label: t({ en: 'Expenses', nb: 'Utlegg' }),
      description: t({
        en: 'Reimbursement requests, receipts and supporting documentation',
        nb: 'Refusjonskrav, kvitteringer og underlagsdokumentasjon',
      }),
    },
    {
      value: 'CALCULATIONS',
      label: t({ en: 'Calculations', nb: 'Beregninger' }),
      description: t({
        en: 'Estimates, quantity take-offs, engineering calculations and cost spreadsheets',
        nb: 'Estimater, mengdeuttak, tekniske beregninger og kostnadsregneark',
      }),
    },
    {
      value: 'OTHER',
      label: t({ en: 'Other', nb: 'Annet' }),
      description: t({
        en: 'Documents that don’t fit any other category',
        nb: 'Dokumenter som ikke passer i noen annen kategori',
      }),
    },
  ];

  return (
    <>
      <Header>
        <HeaderBackButton />
        <SupplementBreadcrumbs>
          <BreadcrumbPage>
            <Icon icon={FolderIcon} />
            {t({ en: 'Files', nb: 'Filer' })}
          </BreadcrumbPage>
        </SupplementBreadcrumbs>
        <HeaderChatButton />
      </Header>
      <MainScrollArea>
        <IndexingStatusAlerts resourceId={supplementId} documentType="supplement" />
        <MainHeader>
          <H1>
            <MainHeaderBackButton
              to="/project/$projectId/workspaces/$workspaceId/contract-management/$supplementId"
              params={{ projectId, workspaceId, supplementId }}
            />
            {t({ en: 'Files', nb: 'Filer' })}
            <FilesPageCount resourceId={supplementId} resourceType="supplement" />
          </H1>
          <FilesPageAddFilesButton resourceId={supplementId} resourceType="supplement" />
        </MainHeader>
        <FilesPageList
          resourceId={supplementId}
          resourceType="supplement"
          filterOptions={filterOptions}
        />
      </MainScrollArea>
      <FileUploadDialog resourceId={supplementId} resourceType="supplement" />
    </>
  );
}
